import { useMutation } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@chain-zen/ui'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { ENABLE_PROJECT44_CARRIER_APP } from '../../../data/mutations/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type TFieldValues = {
  [key: string]: string
}

interface ICustomField {
  disabled?: boolean
  integrationConfigurationID?: string
  isRequired?: boolean
  labelText: string
  name: string
  placeholder?: string
  type?: 'text' | 'select'
  options?: { label: string; value: string }[]
}

type TProject44CarrierCustomer = {
  p44CustomerID: string
  p44ShipmentIdentifierType: 'BILL_OF_LADING' | 'ORDER'
  tmsCustomerID: string
}

type TProject44CarrierEnableAppInput = {
  appType: AppTypeEnum
  username: string
  password: string
  customers: TProject44CarrierCustomer[]
}

type TCustomerGroups = Record<string, ICustomField[]>

const getProject44GroupStructure = (
  uniqueID: string | number,
  disabled?: boolean,
  integrationConfigurationID?: string
) => {
  return {
    [uniqueID]: [
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'Project44 Customer ID',
        name: `p44CustomerID-${uniqueID}`,
        placeholder: 'Enter customer ID from Project44'
      },
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'TMS Customer ID',
        name: `tmsCustomerID-${uniqueID}`,
        placeholder: 'Enter customer ID from TMS'
      },
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'Shipment Identifier Type',
        name: `p44ShipmentIdentifierType-${uniqueID}`,
        type: 'select' as const,
        options: [
          { label: 'Bill of Lading', value: 'BILL_OF_LADING' },
          { label: 'Order Number', value: 'ORDER' }
        ]
      }
    ]
  }
}

const getProject44PayloadStructure = (
  event: Record<string, string>
): { username: string; password: string; customers: TProject44CarrierCustomer[] } => {
  const result: Record<string, string | TProject44CarrierCustomer[]> = {
    username: event.username,
    password: event.password,
    customers: []
  }
  const customerData: Record<string, TProject44CarrierCustomer> = {}
  for (const [key, value] of Object.entries(event)) {
    const match = key.match(/(p44CustomerID|tmsCustomerID|p44ShipmentIdentifierType)-(.+)/)
    if (match) {
      const [, property, id] = match
      if (!customerData[id]) {
        customerData[id] = {
          p44CustomerID: '',
          tmsCustomerID: '',
          p44ShipmentIdentifierType: 'BILL_OF_LADING'
        } as TProject44CarrierCustomer
      }
      if (property === 'p44ShipmentIdentifierType') {
        customerData[id].p44ShipmentIdentifierType = value as 'BILL_OF_LADING' | 'ORDER'
      } else {
        customerData[id][property as keyof Omit<TProject44CarrierCustomer, 'p44ShipmentIdentifierType'>] = value
      }
    }
  }
  result.customers = Object.values(customerData).filter((customer) => customer.p44CustomerID && customer.tmsCustomerID)
  return result as { username: string; password: string; customers: TProject44CarrierCustomer[] }
}

/**
 * Modal for enabling the Project44 Carrier app
 * Allows entering username, password, and customer IDs with shipment identifier types
 */
export function Project44CarrierModal({ appType, isOpen, setModalEditable, onClose }: AppModalProps) {
  const [formError, setFormError] = useState('')
  const [customers, setCustomers] = useState<TCustomerGroups>(() => getProject44GroupStructure(1))
  const [deletedGroups, setDeletedGroups] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<TFieldValues>({
    defaultValues: {
      username: '',
      password: ''
    }
  })

  // Methods for managing customer fields
  const removeFieldsFromSchema = (id: string) => {
    setDeletedGroups((prev) => [...prev, id])
  }

  const handleAddMore = () => {
    const uniqueID = uuidv4()
    const newlyAddedGroup = getProject44GroupStructure(uniqueID)
    setCustomers((old) => {
      return {
        ...old,
        ...newlyAddedGroup
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    reset()
    setCustomers(() => getProject44GroupStructure(1))
    setDeletedGroups([])
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleGroupDelete = (groupID: string) => {
    if (Object.keys(customers).length > 1) {
      removeFieldsFromSchema(groupID)
      setCustomers((old) => {
        const newGroups = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
    }
  }

  // Mutation for enabling Project44 Carrier app
  const [enableProject44CarrierApp, { loading }] = useMutation<
    { enableProject44CarrierApp: { id: string } },
    TProject44CarrierEnableAppInput
  >(ENABLE_PROJECT44_CARRIER_APP, {
    onCompleted: () => {
      toast.success('Project44 Carrier app is enabled successfully!')
      closeModal()
    },
    onError: (err: Error) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    }
  })

  // Handler for form submission
  const handleSaveDetails = (data: TFieldValues) => {
    // Create a copy of the data to avoid modifying the original
    const formData = { ...data }

    // Remove deleted fields from the copy
    deletedGroups.forEach((id) => {
      delete formData[`p44CustomerID-${id}`]
      delete formData[`tmsCustomerID-${id}`]
      delete formData[`p44ShipmentIdentifierType-${id}`]
    })

    const payload = getProject44PayloadStructure(formData)

    if (payload.customers.length === 0) {
      setFormError('At least one customer is required')
      return
    }

    enableProject44CarrierApp({
      variables: {
        username: payload.username,
        password: payload.password,
        appType: appType,
        customers: payload.customers
      }
    })
  }

  const getCustomerFields = () => {
    return Object.entries(customers).map(([groupId, fields]) => (
      <div key={groupId} className="bg-muted p-4 rounded-lg mb-4" role="group" aria-labelledby={`group-${groupId}`}>
        <div className="flex justify-end mb-2">
          {Object.keys(customers).length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleGroupDelete(groupId)}
              aria-label={`Remove customer group ${groupId}`}
            >
              Remove
            </Button>
          )}
        </div>
        {fields.map((field) => {
          if (field.type === 'select') {
            return (
              <div key={field.name} className="space-y-1 mb-4">
                <Label htmlFor={field.name} id={`${field.name}-label`}>
                  {field.labelText}
                  {!!field.isRequired && (
                    <>
                      {' '}
                      <span className="text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">(required)</span>
                    </>
                  )}
                </Label>
                <Select value={watch(field.name) || ''} onValueChange={(value: string) => setValue(field.name, value)}>
                  <SelectTrigger
                    id={field.name}
                    aria-required={field.isRequired}
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                    aria-invalid={!!errors[field.name]}
                  >
                    <SelectValue placeholder={field.placeholder || 'Select an option'} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors[field.name]?.message ? (
                  <p className="text-sm text-red-500" id={`${field.name}-error`} role="alert">
                    {String(errors[field.name]?.message)}
                  </p>
                ) : null}
              </div>
            )
          }
          return (
            <div key={field.name} className="space-y-1 mb-4">
              <Label htmlFor={field.name} id={`${field.name}-label`}>
                {field.labelText}
                {!!field.isRequired && (
                  <>
                    {' '}
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">(required)</span>
                  </>
                )}
              </Label>
              <Input
                id={field.name}
                placeholder={field.placeholder}
                disabled={field.disabled}
                aria-required={field.isRequired}
                aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                aria-invalid={!!errors[field.name]}
                {...register(field.name, {
                  required: field.isRequired ? `${field.labelText} is required` : false
                })}
              />
              {errors[field.name]?.message ? (
                <p className="text-sm text-red-500" id={`${field.name}-error`} role="alert">
                  {String(errors[field.name]?.message)}
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
    ))
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Project44 Carrier for Chain</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Connect your Project44 Carrier account to enable tracking integration.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSaveDetails)} className="space-y-4" noValidate={true}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="username" id="username-label">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your Project44 username"
                aria-required="true"
                aria-describedby={errors.username ? 'username-error' : undefined}
                aria-invalid={!!errors.username}
                {...register('username', { required: 'Username is required' })}
              />
              {errors.username?.message ? (
                <p className="text-sm text-red-500" id="username-error" role="alert">
                  {errors.username.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" id="password-label">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your Project44 password"
                aria-required="true"
                aria-describedby={errors.password ? 'password-error' : undefined}
                aria-invalid={!!errors.password}
                {...register('password', { required: 'Password is required' })}
              />
              {errors.password?.message ? (
                <p className="text-sm text-red-500" id="password-error" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="block text-sm font-medium">
                  Customer Configuration{' '}
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMore}
                  aria-label="Add another customer"
                >
                  Add More
                </Button>
              </div>

              {getCustomerFields()}
            </div>
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0">
            {!!formError && (
              <p className="text-sm text-red-500 self-start" role="alert">
                {formError}
              </p>
            )}
            <div className="flex items-center space-x-2">
              <p className="text-sm text-muted-foreground">
                See{' '}
                <a
                  href="https://handbook.chaineapp.com/product-guide/apps/project44"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary"
                  aria-label="Read the Project44 integration guide (opens in a new tab)"
                >
                  detailed steps here.
                </a>
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || isSubmitting}>
                {loading || isSubmitting ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
